import type { Hotspot } from '../../api/types.js';
import type { HotspotScreenPosition, HotspotsSnapshot } from '../../hotspots/types.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface HotspotPopupProps {
  hotspots: readonly Hotspot[];
  snapshot: HotspotsSnapshot;
  // Position of the active hotspot dot in screen space. May be `undefined`
  // when positions haven't been emitted yet.
  activePosition: HotspotScreenPosition | undefined;
  viewport: { width: number; height: number };
}

export interface HotspotPopupCallbacks {
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const POPUP_WIDTH_FALLBACK = 320;
const POPUP_HEIGHT_FALLBACK = 180;
const ANCHOR_OFFSET_PX = 24;
const VIEWPORT_MARGIN_PX = 12;

function positionPopup(
  popup: HTMLElement,
  active: HotspotScreenPosition | undefined,
  viewport: { width: number; height: number },
): void {
  if (active === undefined || !active.visible) {
    // Fallback: center the popup. Fly-to animation (4s) rotates the camera so
    // the dot becomes visible; until then the popup floats at viewport center.
    popup.style.left = '50%';
    popup.style.top = '50%';
    popup.style.transform = 'translate(-50%, -50%)';
    return;
  }
  const popupW = popup.offsetWidth || POPUP_WIDTH_FALLBACK;
  const popupH = popup.offsetHeight || POPUP_HEIGHT_FALLBACK;
  const left = Math.max(
    VIEWPORT_MARGIN_PX,
    Math.min(viewport.width - popupW - VIEWPORT_MARGIN_PX, active.x - popupW / 2),
  );
  const top = Math.max(
    VIEWPORT_MARGIN_PX,
    Math.min(viewport.height - popupH - VIEWPORT_MARGIN_PX, active.y - popupH - ANCHOR_OFFSET_PX),
  );
  popup.style.left = `${left}px`;
  popup.style.top = `${top}px`;
  popup.style.transform = 'none';
}

export function mountHotspotPopup(
  root: HTMLElement,
  initial: HotspotPopupProps,
  callbacks: HotspotPopupCallbacks,
): ComponentInstance<HotspotPopupProps> {
  // Track the element that had focus before opening so we can restore on close.
  let lastFocused: HTMLElement | null = null;

  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'dialog');
  // aria-modal="false" — popup is non-blocking, the user can still orbit
  // the canvas while it is open.
  wrap.setAttribute('aria-modal', 'false');
  wrap.style.cssText = [
    'position:fixed',
    `width:${POPUP_WIDTH_FALLBACK}px`,
    'background:rgba(15,15,17,0.85)',
    'backdrop-filter:blur(8px)',
    'border:1px solid rgba(255,255,255,0.10)',
    'border-radius:1rem',
    'box-shadow:0 12px 40px rgba(0,0,0,0.45)',
    'overflow:hidden',
    'z-index:12',
    'display:none',
    'pointer-events:auto',
    'color:#f4f4f5',
    'font-family:system-ui,sans-serif',
  ].join(';');

  // Header strip with badge + close button.
  const header = document.createElement('div');
  header.style.cssText =
    'display:flex;align-items:center;justify-content:space-between;padding:0.75rem 1rem 0';

  const badge = document.createElement('span');
  badge.style.cssText =
    'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;color:#d8b4fe;background:rgba(168,85,247,0.18);padding:0.125rem 0.5rem;border-radius:9999px';
  header.appendChild(badge);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.classList.add('viewer-ui-tap-target');
  closeBtn.setAttribute('aria-label', 'Chiudi popup hotspot');
  closeBtn.style.cssText =
    'width:1.75rem;height:1.75rem;border-radius:9999px;background:rgba(0,0,0,0.40);color:#f4f4f5;border:0;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1rem;line-height:1';
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(closeBtn);

  wrap.appendChild(header);

  // Body: title + description.
  const body = document.createElement('div');
  body.style.cssText = 'padding:0.5rem 1rem 0';

  const title = document.createElement('h3');
  title.id = 'hotspot-title';
  title.style.cssText = 'margin:0 0 0.25rem;font-size:0.95rem;font-weight:600;color:#ffffff';
  body.appendChild(title);

  const description = document.createElement('p');
  description.style.cssText =
    'margin:0;font-size:0.75rem;line-height:1.55;color:#a1a1aa;white-space:pre-wrap';
  body.appendChild(description);

  wrap.appendChild(body);

  // Footer: prev/next nav (cyclic).
  const footer = document.createElement('div');
  footer.style.cssText =
    'display:flex;align-items:center;justify-content:space-between;padding:0.75rem 1rem 1rem;gap:0.5rem;margin-top:0.5rem';

  const prevBtn = document.createElement('button');
  prevBtn.type = 'button';
  prevBtn.classList.add('viewer-ui-tap-target');
  prevBtn.setAttribute('aria-label', 'Hotspot precedente');
  prevBtn.textContent = '← Prec';
  prevBtn.style.cssText =
    'background:transparent;color:#d4d4d8;border:1px solid rgba(255,255,255,0.10);border-radius:9999px;padding:0.25rem 0.625rem;font-size:0.6875rem;cursor:pointer;font-family:inherit';
  prevBtn.addEventListener('click', () => callbacks.onPrev());
  footer.appendChild(prevBtn);

  const counter = document.createElement('span');
  counter.style.cssText =
    'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;color:#a1a1aa';
  footer.appendChild(counter);

  const nextBtn = document.createElement('button');
  nextBtn.type = 'button';
  nextBtn.classList.add('viewer-ui-tap-target');
  nextBtn.setAttribute('aria-label', 'Hotspot successivo');
  nextBtn.textContent = 'Succ →';
  nextBtn.style.cssText = prevBtn.style.cssText;
  nextBtn.addEventListener('click', () => callbacks.onNext());
  footer.appendChild(nextBtn);

  wrap.appendChild(footer);

  // Keyboard navigation: ArrowLeft / ArrowRight only fire when focus is
  // inside the popup, so they don't interfere with orbit drag elsewhere.
  wrap.addEventListener('keydown', (ev) => {
    if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      callbacks.onPrev();
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      callbacks.onNext();
    }
  });

  root.appendChild(wrap);

  let currentlyOpen = false;

  const apply = (props: HotspotPopupProps): void => {
    const isOpen = props.snapshot.openIndex >= 0;
    if (!isOpen) {
      if (currentlyOpen) {
        // Restore focus to the dot that opened the popup, if still in DOM.
        const fallback = lastFocused;
        wrap.style.display = 'none';
        currentlyOpen = false;
        if (fallback && document.contains(fallback)) {
          try {
            fallback.focus();
          } catch {
            /* ignore */
          }
        }
      }
      return;
    }
    const idx = props.snapshot.openIndex;
    const hs = props.hotspots[idx];
    if (hs === undefined) {
      wrap.style.display = 'none';
      currentlyOpen = false;
      return;
    }
    const wasOpen = currentlyOpen;
    if (!wasOpen) {
      lastFocused = (document.activeElement as HTMLElement | null) ?? null;
      currentlyOpen = true;
    }
    wrap.style.display = 'block';
    wrap.setAttribute('aria-labelledby', 'hotspot-title');
    setText(badge, `#${idx + 1}`);
    setText(title, hs.title);
    setText(description, hs.description ?? '');
    setText(counter, `${idx + 1} / ${props.hotspots.length}`);
    positionPopup(wrap, props.activePosition, props.viewport);
    // Move focus ONLY on the closed → open transition. Subsequent updates
    // (per-frame hotspot-positions events, prev/next while popup is already
    // open) must NOT steal focus, otherwise prev/next/Tab navigation breaks.
    if (!wasOpen) {
      try {
        closeBtn.focus();
      } catch {
        /* ignore */
      }
    }
  };

  apply(initial);

  return {
    update(props: HotspotPopupProps) {
      apply(props);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
